import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { COLORS_CONFIG_PAGE, COLORS_ADMIN_PAGE } from '../../../conf/constants';
import { Voucher } from '../../types/voucher.types';
import { useDebounce } from '../../hooks/useDebounce';
import { useMunicipalities } from '../../hooks/useMunicipalities';
import MunicipalityDroppableList from './MunicipalityDroppableList';
import AssignedMunicipalityList from './AssignedMunicipalityList';
import MunicipalityTransferButtons from './MunicipalityTransferButtons';
import MunicipalityPagination from './MunicipalityPagination';

interface MunicipalityAssignmentProps {
  currentVoucher: Voucher;
  vouchers: Voucher[];
  onUpdateVouchers: (updatedVouchers: Voucher[]) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  errorHandlers: {
    handleErrorWithLogout: (error: any) => void;
    showSuccessMessage: (message: string) => void;
    showErrorMessage: (message: string) => void;
  };
}

const MunicipalityAssignment: React.FC<MunicipalityAssignmentProps> = ({
  currentVoucher,
  vouchers,
  onUpdateVouchers,
  onSave,
  onCancel,
  isSaving,
  errorHandlers
}) => {
  const [searchMunicipality, setSearchMunicipality] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAvailableMunicipalities, setSelectedAvailableMunicipalities] = useState<number[]>([]);
  const [selectedAssignedMunicipalities, setSelectedAssignedMunicipalities] = useState<number[]>([]);

  const municipalitiesPerPage = 10;
  const debouncedSearchMunicipality = useDebounce(searchMunicipality, 500);

  const [shouldRefetch, setShouldRefetch] = useState(true);

  const {
    allMunicipalities,
    availableMunicipalities,
    loading: municipalitiesLoading,
    error: municipalitiesError,
    totalMunicipalities,
    totalPages,
    retryFetch: handleMunicipalitiesRetry
  } = useMunicipalities(
    currentPage,
    municipalitiesPerPage,
    debouncedSearchMunicipality,
    currentVoucher.cityIds,
    shouldRefetch,
    errorHandlers // Pass error handlers
  );

  const assignedMunicipalities = useMemo(() => {
    if (!currentVoucher || !currentVoucher.cityIds || allMunicipalities.length === 0) return [];
    return allMunicipalities.filter(m => currentVoucher.cityIds.includes(m.id));
  }, [allMunicipalities, currentVoucher]);

  const filteredAvailableMunicipalities = useMemo(() => {
    const assignedIds = currentVoucher.cityIds || [];
    return availableMunicipalities.filter(m => !assignedIds.includes(m.id));
  }, [availableMunicipalities, currentVoucher.cityIds]);

  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setShouldRefetch(true);
    }
  }, [totalPages]);

  const handleSelectAvailableMunicipality = useCallback((id: number) => {
    setSelectedAvailableMunicipalities(prev =>
      prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAssignedMunicipality = useCallback((id: number) => {
    setSelectedAssignedMunicipalities(prev =>
      prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]
    );
  }, []);

  const handleMoveToAssigned = useCallback(() => {
    setShouldRefetch(false);
    
    const updatedVouchers = vouchers.map(v =>
      v.voucherId === currentVoucher.voucherId
        ? { ...v, cityIds: [...v.cityIds, ...selectedAvailableMunicipalities] }
        : v
    );
    onUpdateVouchers(updatedVouchers);
    setSelectedAvailableMunicipalities([]);
    
    setTimeout(() => setShouldRefetch(true), 100);
  }, [currentVoucher.voucherId, vouchers, selectedAvailableMunicipalities, onUpdateVouchers]);

  const handleMoveToAvailable = useCallback(() => {
    setShouldRefetch(false);
    
    const updatedVouchers = vouchers.map(v =>
      v.voucherId === currentVoucher.voucherId
        ? { ...v, cityIds: v.cityIds.filter(mId => !selectedAssignedMunicipalities.includes(mId)) }
        : v
    );
    onUpdateVouchers(updatedVouchers);
    setSelectedAssignedMunicipalities([]);
    
    setTimeout(() => setShouldRefetch(true), 100);
  }, [currentVoucher.voucherId, vouchers, selectedAssignedMunicipalities, onUpdateVouchers]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;

    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    setShouldRefetch(false);

    if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
      const municipality = filteredAvailableMunicipalities[source.index];
      if (municipality) {
        const updatedVouchers = vouchers.map(v =>
          v.voucherId === currentVoucher.voucherId
            ? { ...v, cityIds: [...v.cityIds, municipality.id] }
            : v
        );
        onUpdateVouchers(updatedVouchers);
        setSelectedAvailableMunicipalities([]);
      }
    }

    if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
      const municipality = assignedMunicipalities[source.index];
      if (municipality) {
        const updatedVouchers = vouchers.map(v =>
          v.voucherId === currentVoucher.voucherId
            ? { ...v, cityIds: v.cityIds.filter(mId => mId !== municipality.id) }
            : v
        );
        onUpdateVouchers(updatedVouchers);
        setSelectedAssignedMunicipalities([]);
      }
    }

    setTimeout(() => setShouldRefetch(true), 100);
  }, [filteredAvailableMunicipalities, assignedMunicipalities, currentVoucher.voucherId, vouchers, onUpdateVouchers]);

  React.useEffect(() => {
    setSelectedAvailableMunicipalities([]);
    setSelectedAssignedMunicipalities([]);
    setCurrentPage(1);
    setSearchMunicipality('');
    setShouldRefetch(true);
  }, [currentVoucher.voucherId]);
  React.useEffect(() => {
    setShouldRefetch(true);
  }, [debouncedSearchMunicipality]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{
        fontWeight: 600,
        mb: 2
      }}>
        Available for use in
      </Typography>

      <DragDropContext onDragEnd={currentVoucher.isActive ? handleDragEnd : () => {}}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Assigned Municipalities (Left side) */}
          <AssignedMunicipalityList
            municipalities={assignedMunicipalities}
            selectedMunicipalities={selectedAssignedMunicipalities}
            onSelectMunicipality={handleSelectAssignedMunicipality}
          />

          {/* Transfer Buttons */}
          <MunicipalityTransferButtons
            selectedAvailableMunicipalities={selectedAvailableMunicipalities}
            selectedAssignedMunicipalities={selectedAssignedMunicipalities}
            onMoveToAssigned={handleMoveToAssigned}
            onMoveToAvailable={handleMoveToAvailable}
          />

          {/* Available Municipalities (Right side) */}
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Municipalities"
              variant="outlined"
              value={searchMunicipality}
              onChange={(e) => setSearchMunicipality(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{
                mb: 2,
                bgcolor: COLORS_CONFIG_PAGE.background,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { border: 'none' },
                },
                borderRadius: 3,
                p: 1,
              }}
            />

            <MunicipalityDroppableList
              municipalities={filteredAvailableMunicipalities}
              selectedMunicipalities={selectedAvailableMunicipalities}
              droppableId="available"
              onSelectMunicipality={handleSelectAvailableMunicipality}
              loading={municipalitiesLoading && shouldRefetch}
              error={municipalitiesError}
              onRetry={handleMunicipalitiesRetry}
            />

            {/* Pagination for Municipalities */}
            {!municipalitiesLoading && !municipalitiesError && (
              <MunicipalityPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalMunicipalities={totalMunicipalities}
                debouncedSearchTerm={debouncedSearchMunicipality}
                onPageChange={handlePageChange}
              />
            )}
          </Box>
        </Box>
      </DragDropContext>

      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        mt: 3,
        pt: 2,
        borderTop: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`
      }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isSaving || !currentVoucher.isActive}
          sx={{
            borderColor: COLORS_ADMIN_PAGE.PRIMARY,
            color: COLORS_ADMIN_PAGE.PRIMARY,
            '&:hover': {
              borderColor: COLORS_ADMIN_PAGE.PRIMARY,
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            },
            '&:disabled': {
              borderColor: COLORS_ADMIN_PAGE.BORDER,
              color: COLORS_ADMIN_PAGE.TEXT_SECONDARY
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving || !currentVoucher.isActive}
          sx={{
            bgcolor: COLORS_ADMIN_PAGE.PRIMARY,
            '&:hover': { bgcolor: COLORS_ADMIN_PAGE.PRIMARY },
            '&:disabled': {
              bgcolor: COLORS_ADMIN_PAGE.BORDER,
              color: COLORS_ADMIN_PAGE.TEXT_SECONDARY
            }
          }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default MunicipalityAssignment;